import { processDispositionMetrics, UNCONNECTED_DISPOSITIONS } from '@/lib/metrics'
import { SectionHeader } from './SectionHeader'
import { TransposedDataGrid } from './TransposedDataGrid'
import type { MetricsDataItem, DispositionMetricsItem } from '@/entities/dailyMetric'

interface DispositionTableProps {
  dispositionMetrics: DispositionMetricsItem[]
  metricsData: MetricsDataItem[]
  selectedCampaign: { telephony: string | null } | null
}

export function DispositionTable({ dispositionMetrics, metricsData, selectedCampaign }: DispositionTableProps) {
  const { connectedTypes, unconnectedTypes, dates, createRows } = processDispositionMetrics(
    dispositionMetrics,
    metricsData
  )

  // Helper to add Total column to disposition rows
  const addTotalColumn = (rows: Record<string, string | number>[], isConnected: boolean) => {
    return rows.map((row) => {
      const dispositionType = row.disposition as string

      // Calculate actual count totals for this specific disposition
      let totalDispositionCount = 0
      let totalBaseCount = 0

      dispositionMetrics.forEach((metric) => {
        const rawDispositions = metric.rawDispositions || {}

        // Get the count for this specific disposition type on this date
        const dispositionCount = rawDispositions[dispositionType] || 0
        totalDispositionCount += dispositionCount

        // Calculate base count (connected calls or total calls)
        if (isConnected) {
          // For connected dispositions, base is sum of all connected dispositions
          const connectedCount = Object.entries(rawDispositions)
            .filter(([key]) => !UNCONNECTED_DISPOSITIONS.has(key))
            .reduce((sum: number, [, count]) => sum + (count || 0), 0)
          totalBaseCount += connectedCount
        } else {
          // For unconnected dispositions, base is total calls from metricsData
          const dayMetric = metricsData.find((m) => m.date === metric.date)
          const totalCalls =
            dayMetric?.dialer_metrics?.total_dials || dayMetric?.generalMetrics?.['Total calls'] || 0
          totalBaseCount += totalCalls
        }
      })

      const percentage = totalBaseCount > 0 ? (totalDispositionCount / totalBaseCount) * 100 : 0
      return { ...row, total: percentage > 0 ? `${percentage.toFixed(2)}%` : '' }
    })
  }

  // Check if campaign uses asterisk telephony
  const isAsteriskCampaign = selectedCampaign?.telephony === 'asterisk'

  return (
    <div className="flex flex-col w-full mt-8 space-y-8">
      {/* Connected Dispositions - % of connected calls (always show if present) */}
      {connectedTypes.length > 0 && (
        <div className="flex flex-col w-full">
          <SectionHeader title="Connected Dispositions (% of Connected Calls)" />
          <div className="bg-white rounded-lg shadow-sm border border overflow-hidden">
            <TransposedDataGrid
            rows={addTotalColumn(createRows(connectedTypes, true), true)}
            firstColumn={{
              field: 'disposition',
              headerName: 'Disposition',
            }}
            lastColumn={{
              field: 'total',
              headerName: 'Total',
              type: 'number' as const,
            }}
            dates={dates}
          />
          </div>
        </div>
      )}

      {/* For non-asterisk campaigns: show unconnected dispositions */}
      {!isAsteriskCampaign && unconnectedTypes.length > 0 && (
        <div className="flex flex-col w-full">
          <SectionHeader title="Unconnected Dispositions (% of Total Calls)" />
          <div className="bg-white rounded-lg shadow-sm border border overflow-hidden">
            <TransposedDataGrid
              rows={addTotalColumn(createRows(unconnectedTypes, false), false)}
              firstColumn={{
                field: 'disposition',
                headerName: 'Disposition',
              }}
              lastColumn={{
                field: 'total',
                headerName: 'Total',
                type: 'number' as const,
              }}
              dates={dates}
            />
          </div>
        </div>
      )}
    </div>
  )
}